<script setup>
// GK-HUB: Added agent break/pause functionality (break button, reason picker, resume)
import { computed, h, ref, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
// GK-HUB: ToggleSwitch removed — auto_offline toggle hidden (forced true on backend)

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');

const { isImpersonating } = useImpersonation();

// GK-HUB: Break/pause state
const breakReasons = computed(
  () => store.getters['agentBreaks/getBreakReasons']
);
const isOnBreak = computed(() => currentUserAvailability.value === 'on_break');
const activeBreakSession = computed(
  () => store.getters['agentBreaks/getActiveBreakSession']
);
const showReasonPicker = ref(false);

onMounted(() => {
  store.dispatch('agentBreaks/fetchBreakReasons');
});
// GK-HUB: end break/pause state

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  // GK-HUB: Handle on_break status in the dropdown display
  if (isOnBreak.value) {
    return {
      label: t('AGENT_BREAK.ON_BREAK'),
      color: 'bg-n-amber-9',
    };
  }
  return (
    availabilityStatuses.value.find(status => status.active) ||
    availabilityStatuses.value[0]
  );
});

// GK-HUB: autoOfflineToggle removed — auto_offline forced true on backend

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    // GK-HUB: End break session if changing status while on break
    if (isOnBreak.value) {
      store.dispatch('agentBreaks/endBreak');
    }
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

// GK-HUB: Break/pause actions
async function selectBreakReason(reasonId) {
  try {
    await store.dispatch('agentBreaks/startBreak', {
      breakReasonId: reasonId,
    });
    showReasonPicker.value = false;
  } catch (error) {
    useAlert(t('AGENT_BREAK.START_ERROR'));
  }
}

async function resumeFromBreak() {
  try {
    await store.dispatch('agentBreaks/endBreak');
  } catch (error) {
    useAlert(t('AGENT_BREAK.END_ERROR'));
  }
}

function toggleReasonPicker() {
  showReasonPicker.value = !showReasonPicker.value;
}
// GK-HUB: end break/pause actions
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <div class="grid gap-0">
      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <DropdownContainer>
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              class="min-w-[96px]"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="toggle"
            >
              <div class="flex gap-1 items-center flex-grow text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span>{{ activeStatus.label }}</span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <!-- GK-HUB: auto_offline toggle removed — forced true on backend -->

      <!-- GK-HUB: Break/Pause section -->
      <div class="border-t border-n-weak mx-2 my-1" />

      <!-- Not on break: show pause button -->
      <DropdownItem v-if="!isOnBreak && !showReasonPicker" preserve-open>
        <button
          class="flex items-center gap-2 w-full text-left py-0.5 cursor-pointer bg-transparent border-0 font-[inherit]"
          @click="toggleReasonPicker"
        >
          <Icon icon="i-lucide-coffee" class="size-4 text-n-amber-9" />
          <span class="text-sm text-n-slate-12">
            {{ $t('AGENT_BREAK.START_BREAK') }}
          </span>
        </button>
      </DropdownItem>

      <!-- Reason picker (inline list) -->
      <template v-if="showReasonPicker && !isOnBreak">
        <div class="px-3 pt-2 pb-1">
          <div
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-8 mb-1.5"
          >
            {{ $t('AGENT_BREAK.SELECT_REASON') }}
          </div>
          <div class="grid gap-0.5">
            <button
              v-for="reason in breakReasons"
              :key="reason.id"
              class="flex items-center gap-2 w-full text-left text-[13px] text-n-slate-12 px-2.5 py-2 rounded-md hover:bg-n-amber-9/10 transition-colors cursor-pointer font-[inherit] bg-transparent border-0"
              @click="selectBreakReason(reason.id)"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-n-amber-9/50 shrink-0" />
              {{ reason.name }}
            </button>
          </div>
          <button
            class="text-[11px] text-n-slate-9 mt-1.5 mb-0.5 px-2.5 cursor-pointer bg-transparent border-0 font-[inherit] hover:text-n-slate-11 transition-colors"
            @click="showReasonPicker = false"
          >
            {{ $t('AGENT_BREAK.CANCEL') }}
          </button>
        </div>
      </template>

      <!-- On break: show status + resume -->
      <DropdownItem v-if="isOnBreak" preserve-open>
        <div class="flex items-center justify-between w-full py-0.5">
          <div class="flex items-center gap-2">
            <span class="relative flex h-2.5 w-2.5 shrink-0">
              <span
                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-n-amber-9 opacity-60"
              />
              <span
                class="relative inline-flex rounded-full h-2.5 w-2.5 bg-n-amber-9"
              />
            </span>
            <span
              class="text-sm text-n-amber-9 font-medium truncate max-w-[120px]"
            >
              {{
                activeBreakSession?.break_reason_name ||
                $t('AGENT_BREAK.ON_BREAK')
              }}
            </span>
          </div>
          <button
            class="flex items-center gap-1 text-[12px] font-medium text-n-teal-9 bg-n-teal-9/10 hover:bg-n-teal-9/20 px-2.5 py-1 rounded-md transition-colors cursor-pointer border-0 font-[inherit] shrink-0"
            @click="resumeFromBreak"
          >
            <Icon icon="i-lucide-play" class="size-3" />
            {{ $t('AGENT_BREAK.END_BREAK') }}
          </button>
        </div>
      </DropdownItem>
      <!-- GK-HUB: end Break/Pause section -->
    </div>
  </DropdownSection>
</template>
